import React, { useEffect, useMemo } from "react"
import { Autocomplete, Box, Link, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material"
import { AppNote } from "@components"
import { dsColorNew } from "@palette"
import { DS_REPRICING_ROUTES } from "@constants/routes"
import { dsRepricingDrawerData, dsRepricingDrawerErrors, dsRepricingListData } from "Pages/DsRepricing/Page/@signals"
import { usePricelistCatalog } from "Pages/DsRepricing/Setup/@services/pricelists"
import { useSiteCatalog } from "Pages/DsRepricing/Setup/@services/sites"
import { useAxisCatalog } from "Pages/DsRepricing/Setup/@services/axes"
import { useProductsCatalog } from "Pages/DsRepricing/Setup/@services/useProducts"
import { HelpNote } from "Pages/DsRepricing/Setup/@components/Surface"
import { codeSx, headCellSx } from "Pages/DsRepricing/Setup/@components/formStyles"
import { fmtDelta, kindLabel, pricelistById, productSites, productState, rowSentence, usedBy } from "Pages/DsRepricing/Setup/@services/products"

// Card 2 of Basic, "Which product" (CU-869fhuhy6, brief v3). A strategy
// prices ONE of our products; the product (Setup › Products) says, per
// site, which pricelist is read, the markup cap and which pricelist is
// written (+ derived). The choice is `productId` of the drawer payload
// and travels with the save. Required like the name — publishes
// `product` into the drawer errors and step 1 blocks on it.
const productsHref = `${DS_REPRICING_ROUTES.SETUP_PATH}?tab=products`

// Links in the app's blue, inside notes and under fields
const linkSx = { color: dsColorNew.appBlue, fontWeight: 600, textDecorationColor: 'transparent', '&:hover': { textDecorationColor: dsColorNew.appBlue } }

const ARBITER = 'When two decide the same bracket, the higher-priority one writes; on a tie, the one that moves the price more.'

/** The state the Drawer reads to paint card 2's border. */
export function useProductState() {
	const productId = dsRepricingDrawerData.value?.productId ?? null
	const { items } = usePricelistCatalog()
	const { items: list } = useProductsCatalog()
	return productState(productId, list, items)
}

function Option({ p, others, sites }) {
	const n = (p.rows || []).length
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.25 }}>
			<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle }}>{p.name}</Typography>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				{n} row{n === 1 ? '' : 's'} · {productSites(p).map(sites.labelOf).join(', ') || 'no site yet'}
				{others.length ? ` · used by ${others.map(s => s.name).join(', ')}` : ''}
			</Typography>
		</Box>
	)
}

function ProductInfo({ p, others, items, sites, axes, list }) {
	const cell = { fontSize: 12.5, py: 0.75, px: 1, verticalAlign: 'top' }
	return (
		<HelpNote sx={{ mt: 1.5 }}>
			<Box component="span" sx={{ fontWeight: 600 }}>{p.name}</Box> — on each site the engine reads the current price from the
			<em> read</em> pricelist, caps the broker's markup, and writes the decided net into the <em>write</em> pricelist (and its derived ones).
			<Table size="small" sx={{ mt: 1, mb: 1, bgcolor: dsColorNew.white, borderRadius: '8px', '& td, & th': cell }}>
				<TableHead><TableRow sx={{ '& th': { ...headCellSx, fontSize: 12 } }}>
					<TableCell>Site</TableCell><TableCell>Our offer</TableCell><TableCell>Reads</TableCell><TableCell>Cap</TableCell><TableCell>Writes</TableCell>
				</TableRow></TableHead>
				<TableBody>
					{(p.rows || []).map(r => {
						const write = pricelistById(items, r.writePricelistId)
						const read = pricelistById(items, r.readPricelistId)
						const derived = (r.derived || []).filter(d => d.pricelistId != null)
						return (
							<React.Fragment key={r.id}>
								<TableRow sx={{ '& td': { borderBottom: 'none' } }}>
									<TableCell sx={{ fontWeight: 600 }}>{sites.labelOf(r.siteId)}</TableCell>
									<TableCell>{kindLabel(r, axes)}</TableCell>
									<TableCell>{read ? <Box component="span" sx={codeSx}>{read.code}</Box> : <Box component="em" sx={{ color: dsColorNew.noteAmber }}>no read pricelist yet</Box>}</TableCell>
									<TableCell>{r.capPct != null ? `${r.capPct} %` : <em>none</em>}</TableCell>
									<TableCell>
										{write ? <Box component="span" sx={codeSx}>{write.code}</Box> : <Box component="em" sx={{ color: dsColorNew.noteAmber }}>no write pricelist yet</Box>}
										{derived.length > 0 && <Box sx={{ fontSize: 11.5, color: dsColorNew.textMuted, mt: 0.25 }}>
											↳ {derived.map(d => `${pricelistById(items, d.pricelistId)?.code || '?'} ${fmtDelta(d.delta)}`).join(' · ')}
										</Box>}
									</TableCell>
								</TableRow>
								{/* The same sentence Setup › Products shows under the row (UX v3 D13, criterion 9). */}
								<TableRow>
									<TableCell colSpan={5} sx={{ pt: 0, fontStyle: 'italic', color: dsColorNew.textMuted }}>{rowSentence(r, items, axes, sites.labelOf)}</TableCell>
								</TableRow>
							</React.Fragment>
						)
					})}
				</TableBody>
			</Table>
			The site scope of this strategy (Scope step) is limited to these sites.
			{others.length > 0 && (
				<>
					<br/><Box component="span" sx={{ fontWeight: 600 }}>Also priced by</Box>{' '}
					{others.map((s, i) => <span key={s.id}>{i > 0 ? ' and ' : ''}{s.name} (priority {s.priority}{s.enabled ? '' : ', off'})</span>)}.{' '}
					{ARBITER}
				</>
			)}
			<br/><Link href={productsHref} target="_blank" rel="noopener noreferrer" sx={linkSx}>Edit this product in Setup → Products</Link>
		</HelpNote>
	)
}

export default function DsRepricingWhichProduct() {
	const data = dsRepricingDrawerData.value || {}
	const { items, loading, error } = usePricelistCatalog()
	const products = useProductsCatalog()
	const sites = useSiteCatalog()
	const axes = useAxisCatalog()
	const list = products.items
	const value = data.productId ?? null
	const st = productState(value, list, items)
	const isValid = st.state === 'ok'

	useEffect(() => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.value, product: isValid }
	}, [isValid, data.id])

	const strategies = dsRepricingListData.value
	const othersOf = useMemo(() => (id) => usedBy(id, strategies, data.id ?? null), [strategies, data.id])

	if (loading || products.loading) return <Skeleton variant="rounded" height={40}/>
	if (error) {
		return (
			<AppNote severity="warning">
				Could not load the PMS pricelist catalogue, so the product rows cannot be shown. Reload the page to try again.
			</AppNote>
		)
	}
	if (products.error) {
		return (
			<AppNote severity="warning">
				Could not load the products, so none can be picked.{' '}
				<Link component="button" type="button" onClick={() => products.reload()} sx={{ ...linkSx, fontSize: 'inherit', verticalAlign: 'baseline' }}>Retry</Link>
			</AppNote>
		)
	}

	const selected = st.product
	const others = othersOf(value)
	const fieldError = st.state === 'none' ? st.reason : null

	return (
		<Box>
			{list.length === 0 && (
				<AppNote sx={{ mb: 1.5 }}>
					No product yet. <Link href={productsHref} target="_blank" rel="noopener noreferrer" sx={linkSx}>Create one in Setup → Products</Link> — a strategy cannot be saved without one.
				</AppNote>
			)}
			<Autocomplete size="small"
			              options={list}
			              value={selected}
			              getOptionLabel={(o) => o.name || 'unnamed'}
			              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
			              onChange={(_, next) => { dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, productId: next?.id == null ? null : String(next.id) } }}
			              renderOption={(props, o) => (
			              	<li {...props} key={o.id} style={{ ...props.style, alignItems: 'flex-start' }}>
			              		<Option p={o} others={othersOf(o.id)} sites={sites}/>
			              	</li>
			              )}
			              renderInput={(params) => (
			              	<TextField {...params} label="Product" placeholder="Select the product…"
			              	           error={!!fieldError || st.state === 'invalid'}
			              	           helperText={fieldError || undefined}/>
			              )}/>
			{st.state === 'invalid' && (
				<AppNote severity="error" sx={{ mt: 1.5 }}>
					<strong>{st.reason}</strong>{' '}
					Fix it in <Link href={productsHref} target="_blank" rel="noopener noreferrer" sx={linkSx}>Setup → Products</Link> or pick another product; the strategy stays off until you do.
				</AppNote>
			)}
			{selected && <ProductInfo p={selected} others={others} items={items} sites={sites} axes={axes} list={list}/>}
		</Box>
	)
}
